import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';

// Cores funcionais Kahoot (correspondem ao painel admin)
const ALTERNATIVE_COLORS = ['bg-danger hover:bg-danger/90', 'bg-primary hover:bg-primary/90', 'bg-warning hover:bg-warning/90', 'bg-success hover:bg-success/90'];

const QuestionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { session, currentQuestion, questionIndex, totalQuestions, timeRemaining, timerSeconds, hasAnswered, setHasAnswered, setLastAnswerFeedback, questionResult, sessionReset, clearSessionReset } = useLiveChallenge();

  const [submitting, setSubmitting] = useState(false);
  const [polledChallenge, setPolledChallenge] = useState(null);
  const pollingRef = useRef(null);
  const challengeId = id || session?.live_challenge_id;

  const activeQuestion = currentQuestion;
  const alternatives = activeQuestion?.['live-challenge-alternatives'] || activeQuestion?.live_challenge_alternatives || activeQuestion?.alternatives || [];
  const activeTimerMax = timerSeconds || polledChallenge?.timer_seconds || 30;
  const timedOut = timeRemaining <= 0 && !hasAnswered && activeQuestion;

  useEffect(() => {
    const pollStatus = async () => {
      try {
        const response = await liveChallengesApi.show(challengeId);
        const raw = response?.data;
        if (!raw?.attributes) return;
        const attrs = Object.fromEntries(Object.entries(raw.attributes).map(([k, v]) => [k.replace(/-/g, '_'), v]));
        setPolledChallenge(attrs);
        if (attrs.status === 'finished') {
          clearInterval(pollingRef.current);
          navigate(`/desafio-ao-vivo/${challengeId}/final`, { replace: true });
        }
      } catch {
        // ignore
      }
    };
    pollStatus();
    pollingRef.current = setInterval(pollStatus, 3000);
    return () => clearInterval(pollingRef.current);
  }, [challengeId, navigate]);

  useEffect(() => {
    if (questionResult) {
      navigate(`/desafio-ao-vivo/${challengeId}/resultado`, { replace: true });
    }
  }, [questionResult, navigate, challengeId]);

  // Admin reiniciou a sessão — participante foi destruído no backend, volta ao
  // /entrar para join novo.
  useEffect(() => {
    if (sessionReset) {
      clearSessionReset();
      navigate('/desafio-ao-vivo/entrar', { replace: true });
    }
  }, [sessionReset, clearSessionReset, navigate]);

  const handleAnswer = useCallback(
    async alternativeId => {
      if (hasAnswered || submitting || timedOut) return;
      setSubmitting(true);
      try {
        const response = await liveChallengesApi.answer(challengeId, alternativeId);
        // Backend retorna { data: { is_correct, points_earned, correct_alternative_id } }.
        // Axios entrega o body em response.data, então o feedback "real" está em response.data.data.
        const feedback = response?.data?.data ?? response?.data ?? response;
        setHasAnswered(true);
        setLastAnswerFeedback(feedback);
      } catch (error) {
        const message = error?.response?.data?.error || error?.response?.data?.message || 'Erro ao enviar resposta.';
        toast.error(message);
      } finally {
        setSubmitting(false);
      }
    },
    [hasAnswered, submitting, timedOut, challengeId, setHasAnswered, setLastAnswerFeedback]
  );

  const isDisabled = hasAnswered || submitting || timedOut;
  const qIndex = questionIndex || polledChallenge?.current_question_index || 0;
  const qTotal = totalQuestions || polledChallenge?.total_questions || '?';
  const isUrgent = timeRemaining <= 5;
  const progress = activeTimerMax > 0 ? (timeRemaining / activeTimerMax) * 100 : 0;

  if (!activeQuestion) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-bright px-6">
        <div className="flex flex-col items-center gap-4">
          <div className="flex gap-2">
            <span className="h-3 w-3 animate-bounce rounded-full bg-primary [animation-delay:0ms]" />
            <span className="h-3 w-3 animate-bounce rounded-full bg-primary [animation-delay:150ms]" />
            <span className="h-3 w-3 animate-bounce rounded-full bg-primary [animation-delay:300ms]" />
          </div>
          <p className="m-0 text-base font-semibold text-foreground">Aguardando o professor revelar a questão...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-bright px-4 py-6">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        <header className="flex items-center justify-between">
          <span className="text-sm font-semibold text-neutral">
            Questão {qIndex + 1} de {qTotal}
          </span>

          <div className="relative flex h-16 w-16 items-center justify-center">
            <svg
              className="h-16 w-16 -rotate-90"
              viewBox="0 0 100 100"
            >
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                className="text-neutral-lightest"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (1 - progress / 100)}`}
                strokeLinecap="round"
                className={`transition-all duration-1000 ease-linear ${isUrgent ? 'text-danger' : 'text-primary'}`}
              />
            </svg>
            <span className={`absolute text-base font-bold ${isUrgent ? 'text-danger' : 'text-foreground'}`}>{timeRemaining}</span>
          </div>
        </header>

        <div className="rounded-2xl bg-white px-6 py-8 text-center shadow-dropshadow-md">
          <h2 className="m-0 text-xl font-bold text-foreground md:text-2xl">{activeQuestion?.description}</h2>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {alternatives.map((alt, index) => {
            const colorClasses = ALTERNATIVE_COLORS[index % ALTERNATIVE_COLORS.length];
            return (
              <button
                key={alt.id}
                type="button"
                onClick={() => handleAnswer(alt.id)}
                disabled={isDisabled}
                className={`flex items-center gap-3 rounded-xl p-5 text-left text-base font-bold text-white shadow-dropshadow-md transition-transform duration-150 ease-out-strong active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 ${colorClasses}`}
              >
                <span className="text-lg opacity-80">{String.fromCharCode(65 + index)}</span>
                <span className="flex-1">{alt.description}</span>
              </button>
            );
          })}
        </div>

        {(hasAnswered || timedOut) && (
          <div className="rounded-xl bg-white px-6 py-4 text-center shadow-dropshadow-sm">
            {timedOut ? <p className="m-0 text-base font-semibold text-danger">Tempo esgotado!</p> : <p className="m-0 text-base font-semibold text-primary">Resposta enviada! Aguardando os outros...</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionPage;
